import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, Building, MapPin, Phone, Mail, FileText, DollarSign, Clock, Sparkles, ShieldCheck, BarChart3 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";

// Helper function to get nearby city suggestions
const getNearbyCitySuggestions = (currentCity: string): string[] => {
  const allCities = [
    'Milano', 'Roma', 'Napoli', 'Torino', 'Palermo', 'Genova', 'Bologna', 'Firenze', 'Bari', 'Catania',
    'Venezia', 'Verona', 'Messina', 'Padova', 'Trieste', 'Brescia', 'Parma', 'Taranto', 'Prato', 'Reggio Calabria',
    'Modena', 'Reggio Emilia', 'Perugia', 'Livorno', 'Ravenna', 'Cagliari', 'Foggia', 'Rimini', 'Salerno', 'Ferrara',
    'Sassari', 'Monza', 'Bergamo', 'Forlì', 'Trento', 'Vicenza', 'Terni', 'Bolzano', 'Novara', 'Piacenza',
    'Ancona', 'Andria', 'Arezzo', 'Udine', 'Cesena', 'Lecce', 'Pesaro', 'La Spezia', 'Brindisi', 'Pisa',
    'Como', 'Varese', 'Treviso', 'Busto Arsizio', 'Sesto San Giovanni', 'Pozzuoli'
  ];
  
  // Filter out current city and return first 8 suggestions
  return allCities
    .filter(city => city.toLowerCase() !== currentCity?.toLowerCase())
    .slice(0, 8);
};

const companyInfoSchema = z.object({
  company_name: z.string().min(1, "Company name is required"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  address: z.string().min(1, "Address is required"),
  city: z.string().min(1, "City is required"),
  postal_code: z.string().min(1, "Postal code is required"),
  country: z.string().min(1, "Country is required"),
  tax_id: z.string().optional(),
  phone: z.string().min(1, "Phone number is required"),
  email: z.string().email("Valid email is required"),
  website: z.string().url().optional().or(z.literal("")),
  primary_categories: z.array(z.string()).default([]),
  service_areas: z.array(z.string()).default([]),
});

const serviceSchema = z.object({
  name: z.string().min(1, "Service name is required"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  category_id: z.string().min(1, "Category is required"),
  base_price: z.number().min(0.01, "Price must be greater than 0"),
  pricing_type: z.enum(["fixed", "hourly", "per_sqm", "custom"]),
  duration_minutes: z.number().min(15, "Duration must be at least 15 minutes"),
  included_items: z.array(z.string()).default([]),
  benefit_items: z.array(z.string()).default([]),
  included_other: z.string().optional(),
  benefit_other: z.string().optional(),
  allowed_service_types: z.array(z.string()).default([]),
  accepted_payment_methods: z.array(z.string()).default(["card","cash"]),
});

type CompanyInfoForm = z.infer<typeof companyInfoSchema>;
type ServiceForm = z.infer<typeof serviceSchema>;

export default function CompanyOnboarding() {
  const [currentStep, setCurrentStep] = useState(2);
  const [user, setUser] = useState(null);
  const [companyIdState, setCompanyIdState] = useState<string | null>(null);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState<ServiceForm[]>([]);
  const [acceptedVendorPaymentsTerms, setAcceptedVendorPaymentsTerms] = useState(false);
  const [availability, setAvailability] = useState({
    monday: { start: "09:00", end: "17:00", available: true },
    tuesday: { start: "09:00", end: "17:00", available: true },
    wednesday: { start: "09:00", end: "17:00", available: true },
    thursday: { start: "09:00", end: "17:00", available: true },
    friday: { start: "09:00", end: "17:00", available: true },
    saturday: { start: "09:00", end: "15:00", available: true },
    sunday: { start: "00:00", end: "00:00", available: false },
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();

  const companyForm = useForm<CompanyInfoForm>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      company_name: "",
      description: "",
      address: "",
      city: "",
      postal_code: "",
      country: "",
      tax_id: "",
      phone: "",
      email: "",
      website: "",
      primary_categories: [],
      service_areas: [],
    },
  });

  const serviceForm = useForm<ServiceForm>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      name: "",
      description: "",
      category_id: "",
      base_price: 0,
      pricing_type: "fixed",
      duration_minutes: 60,
    },
  });

  useEffect(() => {
    checkAuthAndRedirect();
    fetchCategories();
  }, []);

  const checkAuthAndRedirect = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }

    // Same company resolution as dashboard: prefer latest completed; else latest active draft.
    // Do NOT use profile->companies[0] — order is undefined with multiple rows and caused bogus “restart” loops + duplicate companies.
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("user_id", session.user.id)
      .maybeSingle();

    if (profile?.id) {
      let companyRow: { id: string; onboarding_completed: boolean | null } | null = null;

      const { data: completedList } = await supabase
        .from("companies")
        .select("id, onboarding_completed")
        .eq("profile_id", profile.id)
        .eq("is_deleted", false)
        .eq("is_active", true)
        .eq("onboarding_completed", true)
        .order("created_at", { ascending: false })
        .limit(1);

      if (completedList && completedList.length > 0) {
        companyRow = completedList[0] as { id: string; onboarding_completed: boolean | null };
      } else {
        const { data: activeList } = await supabase
          .from("companies")
          .select("id, onboarding_completed")
          .eq("profile_id", profile.id)
          .eq("is_deleted", false)
          .eq("is_active", true)
          .order("created_at", { ascending: false })
          .limit(1);
        if (activeList && activeList.length > 0) {
          companyRow = activeList[0] as { id: string; onboarding_completed: boolean | null };
        }
      }

      if (companyRow?.onboarding_completed) {
        navigate("/company/dashboard");
        return;
      }

      if (companyRow?.id && !companyRow.onboarding_completed) {
        setCompanyIdState(companyRow.id);
        try {
          localStorage.setItem("onboardingCompanyId", companyRow.id);
        } catch {
          /* ignore */
        }
      }
    }

    setUser(session.user);
    companyForm.setValue("email", session.user.email || "");
    companyForm.setValue("company_name", (session.user as any)?.user_metadata?.full_name || "");
    companyForm.setValue("country", companyForm.getValues("country") || "Italy");
  };

  const fetchCategories = async () => {
    const { data } = await supabase
      .from("service_categories")
      .select("*")
      .order("name");
    
    if (data) setCategories(data);
  };

  const getOnboardingCompanyId = useCallback(async (): Promise<string | null> => {
    let companyId: string | null = companyIdState;
    if (!companyId) {
      try {
        companyId = localStorage.getItem("onboardingCompanyId");
      } catch {
        /* ignore */
      }
    }
    if (!companyId && user?.id) {
      const profRes = await supabase.from("profiles").select("id").eq("user_id", user.id).maybeSingle();
      const profileId = profRes.data?.id as string | undefined;
      if (profileId) {
        const compRes = await supabase
          .from("companies")
          .select("id")
          .eq("profile_id", profileId)
          .eq("is_deleted", false)
          .eq("is_active", true)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        companyId = (compRes.data as { id?: string } | null)?.id ?? null;
      }
    }
    return companyId;
  }, [companyIdState, user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) return;
      const companyId = await getOnboardingCompanyId();
      if (!companyId || cancelled) return;
      const { data, error } = await supabase
        .from("companies")
        .select("vendor_payments_terms_accepted")
        .eq("id", companyId)
        .maybeSingle();
      if (cancelled || error) return;
      const row = data as { vendor_payments_terms_accepted?: boolean } | null;
      if (row && typeof row.vendor_payments_terms_accepted === "boolean") {
        setAcceptedVendorPaymentsTerms(row.vendor_payments_terms_accepted);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, companyIdState, getOnboardingCompanyId]);

  const handleCompanyInfoSubmit = async (data: CompanyInfoForm) => {
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      // Create profile if missing (e.g., after deletion+anonymization or first login path)
      let profileId = profile?.id as string | undefined;
      if (!profileId) {
        const fullNameFallback = (user as any)?.user_metadata?.full_name || (user as any)?.email?.split("@")[0] || "User";
        const { data: created, error: createErr } = await (supabase as any)
          .from("profiles")
          .insert({
            user_id: (user as any).id,
            email: (user as any).email,
            full_name: fullNameFallback,
            user_type: "company",
            is_deleted: false
          })
          .select("id")
          .single();
        if (createErr) {
          toast({ title: "Error", description: createErr.message, variant: "destructive" });
          return;
        }
        profileId = created?.id as string;
      }

      // Update profile to company type and ensure it's not marked deleted
      await supabase
        .from("profiles")
        .update({
          user_type: "company",
          is_deleted: false,
          email: (user as any).email,
          full_name: (user as any)?.user_metadata?.full_name || (user as any)?.email?.split("@")[0] || "User"
        })
        .eq("id", profileId);

      const companyPayload = {
        company_name: data.company_name,
        description: data.description,
        address: data.address,
        city: data.city,
        postal_code: data.postal_code,
        country: data.country,
        tax_id: data.tax_id || null,
        phone: data.phone,
        email: data.email,
        website: data.website || null,
        is_active: true,
        is_deleted: false,
      };

      const { data: alreadyDone } = await supabase
        .from("companies")
        .select("id")
        .eq("profile_id", profileId)
        .eq("is_deleted", false)
        .eq("is_active", true)
        .eq("onboarding_completed", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (alreadyDone?.id) {
        navigate("/company/dashboard");
        return;
      }

      const { data: draftCompany } = await supabase
        .from("companies")
        .select("id")
        .eq("profile_id", profileId)
        .eq("is_deleted", false)
        .eq("is_active", true)
        .eq("onboarding_completed", false)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      let resolvedCompanyId: string | null = null;

      if (draftCompany?.id) {
        const { error: updErr } = await supabase
          .from("companies")
          .update(companyPayload)
          .eq("id", draftCompany.id);
        if (updErr) throw updErr;
        resolvedCompanyId = draftCompany.id;
      } else {
        const ins = await supabase
          .from("companies")
          .insert({
            profile_id: profileId,
            ...companyPayload,
            share_enabled: false,
          })
          .select("id")
          .single();
        if (ins.error) throw ins.error;
        resolvedCompanyId = ins.data?.id || null;
      }

      setCompanyIdState(resolvedCompanyId);
      try {
        if (resolvedCompanyId) localStorage.setItem("onboardingCompanyId", resolvedCompanyId);
      } catch {
        /* ignore */
      }

      setCurrentStep(3);
      toast({
        title: "Success",
        description: draftCompany?.id ? "Dati azienda aggiornati." : "Company information saved successfully!",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Unknown error",
        variant: "destructive",
      });
    }
  };

  const [serviceTagsInput, setServiceTagsInput] = useState("");
  const [serviceTags, setServiceTags] = useState<string[]>([]);
  const suggestedIncluded = [
    "Deep cleaning of all rooms",
    "Bathroom sanitization",
    "Kitchen deep clean",
    "Floor mopping & vacuuming",
    "Dusting & surface cleaning",
  ];
  const suggestedBenefits = [
    "Insured & Bonded",
    "Response within 2 hours",
    "Professional Equipment",
    "24/7 Support",
  ];
  const cleaningServiceTypeChecklist = [
    { key: 'standard', label: 'Standard', explanation: 'Pulizia regolare: pavimenti, superfici, bagni, aspirazione e spolvero.' },
    { key: 'deep_clean', label: 'Deep Clean', explanation: 'Pulizia profonda: interni mobili, fughe, apparecchiature, punti difficili.' },
    { key: 'eco_friendly', label: 'Eco-Friendly', explanation: 'Prodotti ecosostenibili, ipoallergenici, a bassa impronta ambientale.' },
    { key: 'premium', label: 'Premium', explanation: 'Team senior, materiali premium, extra dettagli e finiture.' },
  ];
  const [vendorCoversFee, setVendorCoversFee] = useState(false);
  const [displayFeeDisclaimer, setDisplayFeeDisclaimer] = useState(false);

  const handleServiceSubmit = (data: ServiceForm) => {
    const included = [...(data.included_items || []), ...(data.included_other ? [data.included_other] : [])];
    const benefits = [...(data.benefit_items || []), ...(data.benefit_other ? [data.benefit_other] : [])];
    const normalizedBasePrice = Math.round((Number(data.base_price || 0) + Number.EPSILON) * 100) / 100;
    // Onboarding doesn't expose the pricing-rules editor (the ServiceBuilder
    // does, post-onboarding on the dashboard). For fixed/custom services we
    // start with pricing rules DISABLED so customers don't get asked about
    // sqm/type at booking time when the vendor never configured those values.
    // For per_sqm/hourly the runtime forces enabled=true regardless, so the
    // flag here doesn't matter. The vendor can flip the toggle anytime from
    // the dashboard ServiceBuilder. Server-side normalizePricingRules then
    // fills the rest of the structure consistently.
    const ruleSensitivePricing = data.pricing_type === 'per_sqm' || data.pricing_type === 'hourly';
    const enriched: any = {
      ...data,
      base_price: normalizedBasePrice,
      tags: serviceTags,
      included_items: included,
      benefit_items: benefits,
      vendor_covers_customer_fee: vendorCoversFee,
      display_fee_disclaimer: displayFeeDisclaimer,
      pricing_rules: { enabled: ruleSensitivePricing },
    };
    setServices([...services, enriched]);
    setServiceTags([]);
    setServiceTagsInput("");
    serviceForm.reset();
    toast({
      title: "Success",
      description: "Service added successfully!",
    });
  };

  const handleAvailabilitySubmit = async () => {
    try {
      // Prefer in-memory company id captured during step 1; fallback to resolving by profile
      // Try in-memory, then localStorage, then DB fallback
      let companyId = companyIdState;
      if (!companyId) {
        try { companyId = localStorage.getItem('onboardingCompanyId'); } catch (_) {}
      }
      if (!companyId) {
        const profRes = await (supabase as any)
          .from("profiles")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();
        const profileId = profRes.data?.id;
        if (!profileId) throw new Error("Profile not found");

        const compRes = await (supabase as any)
          .from("companies")
          .select("id")
          .eq("profile_id", profileId)
          .eq('is_deleted', false)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        companyId = compRes.data?.id || null;
      }
      if (!companyId) throw new Error("Company not found");

      // First, delete any existing availability for this company
      const { error: deleteError } = await supabase
        .from("company_availability")
        .delete()
        .eq("company_id", companyId);

      if (deleteError) {
        console.error("Error deleting existing availability:", deleteError);
        // Continue anyway, might be first time
      }

      // Insert availability
      const availabilityToInsert = Object.entries(availability).map(([day, schedule]) => ({
        company_id: companyId,
        day_of_week: getDayNumber(day),
        start_time: schedule.start,
        end_time: schedule.end,
        is_available: schedule.available,
      }));

      const { error: availabilityError } = await supabase
        .from("company_availability")
        .insert(availabilityToInsert);

      if (availabilityError) {
        console.error("Availability insert error:", availabilityError);
        throw availabilityError;
      }

      setCurrentStep(5);
      toast({
        title: "Success",
        description: "Availability saved successfully!",
      });
    } catch (error: any) {
      console.error("Availability submission error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save availability. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleFinishOnboarding = async () => {
    try {
      if (!acceptedVendorPaymentsTerms) {
        toast({
          title: "Error",
          description: "Accetta prima le condizioni su pagamenti e payout.",
          variant: "destructive",
        });
        return;
      }

      if (services.length === 0) {
        toast({
          title: "Error",
          description: "Please add at least one service.",
          variant: "destructive",
        });
        return;
      }

      // Prefer in-memory company id captured during step 1; fallback to resolving by profile
      // Try in-memory, then localStorage, then DB fallback
      let companyId = companyIdState;
      if (!companyId) {
        try { companyId = localStorage.getItem('onboardingCompanyId'); } catch (_) {}
      }
      if (!companyId) {
        const profRes = await (supabase as any)
          .from("profiles")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();
        const profileId = profRes.data?.id;
        if (!profileId) throw new Error("Profile not found");

        const compRes = await (supabase as any)
          .from("companies")
          .select("id")
          .eq("profile_id", profileId)
          .eq('is_deleted', false)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        companyId = compRes.data?.id || null;
      }
      if (!companyId) throw new Error("Company not found");

      console.log("Inserting services for company:", companyId); // Debug log

      // Insert services via secure API (server-side), to bypass client RLS while keeping policies strict
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('/api/company/services/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ company_id: companyId, services })
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.error || 'Failed to add services');
      }

      // API call already validated and throws on failure above

      console.log("Services inserted successfully, marking onboarding as completed"); // Debug log

      // Mark onboarding as completed
      const { error: updateError } = await supabase
        .from("companies")
        .update({ 
          onboarding_completed: true,
          primary_categories: companyForm.getValues("primary_categories"),
          service_areas: companyForm.getValues("service_areas"),
          share_enabled: true,
          vendor_payments_terms_accepted: true,
        })
        .eq("id", companyId);

      if (updateError) {
        console.error("Error updating onboarding status:", updateError);
        throw updateError;
      }

      console.log("Onboarding completed successfully, redirecting to dashboard"); // Debug log

      toast({
        title: "Success",
        description: "Onboarding completed successfully!",
      });

      navigate("/company/dashboard");
    } catch (error: any) {
      console.error("Finish onboarding error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to complete onboarding. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getDayNumber = (day: string) => {
    const dayMap = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    return dayMap[day];
  };

  const removeService = (index: number) => {
    setServices(services.filter((_, i) => i !== index));
  };

  const handlePaymentsStepContinue = () => {
    if (!acceptedVendorPaymentsTerms) {
      toast({
        title: "Attenzione",
        description: "Per continuare devi accettare le condizioni su pagamenti e payout.",
        variant: "destructive",
      });
      return;
    }

    setCurrentStep(6);
  };

  const progress = (currentStep / 6) * 100;

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="app-page p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="app-dark-panel mb-8 overflow-hidden p-6 md:p-8">
          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <div>
              <Badge className="mb-4 border-white/10 bg-white/8 text-slate-100 hover:bg-white/8">
                Onboarding azienda
              </Badge>
              <h1 className="text-3xl font-bold text-white mb-2">Configura il tuo profilo azienda</h1>
              <p className="max-w-2xl text-slate-300">{t('onboard.welcome.subtitle','Ti guidiamo passo dopo passo nella configurazione del profilo, dei servizi e della disponibilità')}</p>
              <Progress value={progress} className="mt-5 max-w-md bg-white/10" />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="app-dark-metric p-4">
                <Sparkles className="mb-3 h-5 w-5 text-sky-300" />
                <div className="text-sm font-semibold text-white">Dati chiari</div>
                <p className="mt-1 text-xs leading-5 text-slate-300">Inserisci le informazioni che servono ai clienti per capire subito chi sei e dove lavori.</p>
              </div>
              <div className="app-dark-metric p-4">
                <BarChart3 className="mb-3 h-5 w-5 text-violet-300" />
                <div className="text-sm font-semibold text-white">Servizi leggibili</div>
                <p className="mt-1 text-xs leading-5 text-slate-300">Prezzi, cosa includi e modalità operative restano in un unico flusso ordinato.</p>
              </div>
              <div className="app-dark-metric p-4">
                <ShieldCheck className="mb-3 h-5 w-5 text-emerald-300" />
                <div className="text-sm font-semibold text-white">Attivazione corretta</div>
                <p className="mt-1 text-xs leading-5 text-slate-300">Mantieni una configurazione completa e coerente con i dati che verranno salvati nel profilo.</p>
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {[
              "1. Account azienda creato",
              "2. Dati essenziali azienda",
              "3. Servizi principali",
              "4. Zone coperte e disponibilità",
              "5. Pagamenti e documenti",
              "6. Riepilogo finale",
            ].map((stepLabel, index) => {
              const stepNumber = index + 1;
              const isActive = stepNumber === currentStep;
              const isCompleted = stepNumber < currentStep || stepNumber === 1;

              return (
              <Badge
                key={stepLabel}
                className={
                  isActive
                    ? "border-sky-300/20 bg-sky-400/15 text-sky-100 hover:bg-sky-400/15"
                    : isCompleted
                      ? "border-emerald-300/20 bg-emerald-400/15 text-emerald-100 hover:bg-emerald-400/15"
                      : "border-white/10 bg-white/6 text-slate-300 hover:bg-white/6"
                }
              >
                {stepLabel}
              </Badge>
            )})}
          </div>
        </div>

        {currentStep === 2 && (
          <Card className="app-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                {t('onboard.company.title','Parlaci della tua azienda')}
              </CardTitle>
              <CardDescription>
                {t('onboard.company.desc','Questi dati servono a creare il profilo pubblico azienda e a far capire ai clienti dove operi e come contattarti')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...companyForm}>
                <form onSubmit={companyForm.handleSubmit(handleCompanyInfoSubmit)} className="space-y-6">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    Hai già creato l&apos;account azienda. Ora completi il profilo con i dati essenziali che verranno salvati esattamente come prima, ma in un percorso più guidato.
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-base font-semibold text-slate-900">Dati principali</h3>
                    <p className="text-sm text-muted-foreground">
                      Queste informazioni sono la base del profilo azienda e vengono usate per identificarti correttamente in piattaforma.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={companyForm.control}
                      name="company_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboard.company.name','Ragione sociale o nome azienda')}</FormLabel>
                          <FormControl>
                            <Input placeholder="Es. Impresa Pulita Venezia" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={companyForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboard.company.email','Email di lavoro')}</FormLabel>
                          <FormControl>
                            <Input placeholder="company@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-base font-semibold text-slate-900">Come ti presenti ai clienti</h3>
                    <p className="text-sm text-muted-foreground">
                      Qui raccogliamo le informazioni che aiutano a spiegare cosa fai, dove sei e come contattarti.
                    </p>
                  </div>

                  <FormField
                    control={companyForm.control}
                    name="description"
                    render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboard.company.description','Presentazione azienda')}</FormLabel>
                        <FormControl>
                            <Textarea 
                              placeholder={t('onboard.company.description_ph','Spiega in poche righe chi siete, quali servizi fate meglio e perché un cliente dovrebbe scegliervi...')}
                            rows={4}
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={companyForm.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>{t('onboard.company.address','Indirizzo sede o riferimento operativo')}</FormLabel>
                          <FormControl>
                            <Input placeholder="Es. Via Roma 25" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={companyForm.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboard.company.city','Città principale')}</FormLabel>
                          <FormControl>
                            <Input placeholder="Es. Venezia" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={companyForm.control}
                      name="postal_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboard.company.postal_code','CAP')}</FormLabel>
                          <FormControl>
                            <Input placeholder="00100" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={companyForm.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboard.company.country','Paese')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t('onboard.company.select_country','Seleziona il paese')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {/* Minimal list for now; can be expanded or localized */}
                              <SelectItem value="Italy">Italy</SelectItem>
                              <SelectItem value="France">France</SelectItem>
                              <SelectItem value="Germany">Germany</SelectItem>
                              <SelectItem value="Spain">Spain</SelectItem>
                              <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                              <SelectItem value="United States">United States</SelectItem>
                              <SelectItem value="Canada">Canada</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={companyForm.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboard.company.phone','Telefono di contatto')}</FormLabel>
                          <FormControl>
                            <Input placeholder="+39 345 123 4567" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={companyForm.control}
                      name="tax_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboard.company.tax_id','Partita IVA o codice fiscale azienda (opzionale)')}</FormLabel>
                          <FormControl>
                            <Input placeholder="Es. IT12345678901" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={companyForm.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                          <FormLabel>{t('onboard.company.website','Sito web (opzionale)')}</FormLabel>
                        <FormControl>
                          <Input placeholder="https://www.tuaazienda.it" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full">
                    {t('onboard.actions.continue_services','Salva dati essenziali e continua')}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        )}

        {currentStep === 3 && (
          <div className="space-y-6">
            <Card className="app-panel">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  {t('onboard.services.title','Servizi principali')}
                </CardTitle>
                <CardDescription>
                  {t('onboard.services.desc','Configura il servizio principale da pubblicare: nome, categoria, descrizione, prezzo e durata')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...serviceForm}>
                  <form onSubmit={serviceForm.handleSubmit(handleServiceSubmit)} className="space-y-4">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                      In questo step definisci il servizio che verrà salvato sul profilo azienda. Le impostazioni legate a pagamenti e documentazione arrivano nello step successivo dedicato.
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-base font-semibold text-slate-900">Servizio base</h3>
                      <p className="text-sm text-muted-foreground">
                        Inserisci il servizio così come vuoi che venga compreso da cliente e piattaforma.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={serviceForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('onboard.services.name','Nome del servizio')}</FormLabel>
                            <FormControl>
                              <Input placeholder="Es. Pulizia appartamento" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={serviceForm.control}
                        name="category_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('onboard.services.category','Categoria')}</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder={t('onboard.services.select_category','Seleziona la categoria più adatta')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {categories.map((category) => (
                                  <SelectItem key={category.id} value={category.id}>
                                    {category.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={serviceForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('onboard.services.description','Descrizione del servizio')}</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder={t('onboard.services.description_ph','Spiega cosa comprende il servizio, per chi è adatto e cosa ti distingue...')}
                              rows={3}
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Cleaning-only: Allowed Service Types checklist */}
                    <div>
                      <div className="space-y-2">
                        <h3 className="text-base font-semibold text-slate-900">Tipologia di intervento</h3>
                        <p className="text-sm text-muted-foreground">
                          Se fai pulizie, specifica quali varianti offri davvero. Questo evita richieste non coerenti con il tuo servizio.
                        </p>
                      </div>
                      <FormLabel className="mt-3 block">{t('onboard.services.types_title','Tipologie di servizio (solo pulizie)')}</FormLabel>
                      <div className="text-xs text-muted-foreground mb-2">{t('onboard.services.types_helper','Conferma le tipologie offerte. Ai clienti verranno mostrate solo quelle selezionate.')}</div>
                      <div className="space-y-3">
                        {cleaningServiceTypeChecklist.map((opt) => {
                          const selected = (serviceForm.watch('allowed_service_types') || []).includes(opt.key);
                          return (
                            <div key={opt.key} className="flex items-start gap-3 p-3 border rounded">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => {
                                  const current = serviceForm.getValues('allowed_service_types') || [];
                                  serviceForm.setValue(
                                    'allowed_service_types',
                                    e.target.checked ? [...current, opt.key] : current.filter((x: string) => x !== opt.key)
                                  );
                                }}
                                className="mt-1"
                              />
                              <div>
                                <div className="font-medium">{opt.label}</div>
                                <div className="text-sm text-muted-foreground">{opt.explanation}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Service Tags (chips) */}
                    <div>
                      <FormLabel>{t('onboard.services.tags_title','Parole chiave e sinonimi')}</FormLabel>
                      <div className="text-xs text-muted-foreground mt-1">
                        Aggiungi termini utili con cui i clienti potrebbero cercarti, anche in forma colloquiale.
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          placeholder={t('onboard.services.tags_placeholder','Aggiungi una parola chiave e premi Invio, ad esempio pulizie, vetri, turnover')}
                          value={serviceTagsInput}
                          onChange={(e) => setServiceTagsInput(e.target.value)}
                          onKeyDown={(e) => {
                            const v = serviceTagsInput.trim();
                            if (e.key === 'Enter' && v) {
                              e.preventDefault();
                              if (!serviceTags.includes(v)) setServiceTags([...serviceTags, v]);
                              setServiceTagsInput("");
                            }
                          }}
                        />
                      </div>
                      {serviceTags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {serviceTags.map((t, idx) => (
                            <Badge key={idx} variant="secondary" className="cursor-pointer" onClick={() => setServiceTags(serviceTags.filter((x) => x !== t))}>{t}</Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-base font-semibold text-slate-900">Prezzo e durata</h3>
                      <p className="text-sm text-muted-foreground">
                        Qui definisci il prezzo di partenza e la durata standard che verranno associati al servizio.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={serviceForm.control}
                        name="pricing_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('onboard.services.pricing_type','Come calcoli il prezzo')}</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="fixed">{t('onboard.services.pricing.fixed','Fixed Price')}</SelectItem>
                                <SelectItem value="hourly">{t('onboard.services.pricing.hourly','Per Hour')}</SelectItem>
                                <SelectItem value="per_sqm">{t('onboard.services.pricing.per_sqm','Per Square Meter')}</SelectItem>
                                <SelectItem value="custom">{t('onboard.services.pricing.custom','Custom')}</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={serviceForm.control}
                        name="base_price"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('onboard.services.base_price','Prezzo di partenza (€)')}</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                step="0.01"
                                placeholder="50.00"
                                {...field}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={serviceForm.control}
                        name="duration_minutes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('onboard.services.duration','Durata standard (minuti)')}</FormLabel>
                            <FormControl>
                              <Input 
                                type="number"
                                placeholder="60"
                                {...field}
                                onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Fee coverage & disclaimer */}
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <h3 className="text-base font-semibold text-slate-900">Commissioni mostrate al cliente</h3>
                        <p className="text-sm text-muted-foreground">
                          Queste impostazioni non cambiano il salvataggio dei dati: ti aiutano solo a definire come presentare le fee nel riepilogo cliente.
                        </p>
                      </div>
                      <div className="flex items-start gap-3 p-3 border rounded">
                        <input
                          type="checkbox"
                          checked={vendorCoversFee}
                          onChange={(e) => setVendorCoversFee(e.target.checked)}
                          className="mt-1"
                        />
                        <div>
                          <div className="font-medium">{t('onboard.services.vendor_covers_fee.title','Vuoi assorbire tu la fee mostrata al cliente?')}</div>
                          <div className="text-sm text-muted-foreground">{t('onboard.services.vendor_covers_fee.desc','Se attivi questa opzione, la commissione lato cliente resta a carico tuo e il prezzo mostrato può riflettere questa scelta.')}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 border rounded">
                        <input
                          type="checkbox"
                          checked={displayFeeDisclaimer}
                          onChange={(e) => setDisplayFeeDisclaimer(e.target.checked)}
                          className="mt-1"
                        />
                        <div>
                          <div className="font-medium">{t('onboard.services.show_fee_disclaimer.title','Mostrare una nota esplicativa al cliente')}</div>
                          <div className="text-sm text-muted-foreground">{t('onboard.services.show_fee_disclaimer.desc','Se attivo, nel riepilogo comparirà una nota del tipo: “Le fee cliente sono coperte dal fornitore.”')}</div>
                        </div>
                      </div>
                    </div>

                    <Button type="submit">
                      {t('onboard.services.add_service','Salva servizio')}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>

            {services.length > 0 && (
              <Card className="app-panel">
                  <CardHeader>
                  <CardTitle>{t('onboard.services.added_services','Added Services ({{count}})').replace('{{count}}', String(services.length))}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {services.map((service: any, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <h4 className="font-medium">{service.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {formatCurrency(Number(service.base_price || 0))} • {service.duration_minutes}min • {service.pricing_type}
                          </p>
                          {service.tags?.length ? (
                            <p className="text-xs text-muted-foreground mt-1">Tags: {service.tags.join(', ')}</p>
                          ) : null}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => removeService(index)}
                        >
                          {t('onboard.services.remove','Remove')}
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                    <Button variant="outline" onClick={() => setCurrentStep(2)} className="sm:flex-1">
                      Torna ai dati azienda
                    </Button>
                    <Button onClick={() => setCurrentStep(4)} className="sm:flex-1">
                      {t('onboard.services.continue_availability','Continua a zone e disponibilità')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {currentStep === 4 && (
          <Card className="app-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Zone coperte e disponibilità
              </CardTitle>
              <CardDescription>
                Definisci dove lavori e in quali giorni/orari sei normalmente disponibile. I dati continueranno a essere salvati con la stessa logica attuale.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  Qui stai completando copertura geografica e disponibilità operativa standard dell&apos;azienda. Potrai modificarle anche in seguito.
                </div>
                <div>
                  <Label>{t('onboard.company.primary_categories','Quali servizi offrite principalmente?')}</Label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Seleziona le categorie che descrivono meglio la tua attività principale. Servono a classificare correttamente il profilo.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[
                      "cleaning","gardening","maintenance","plumber","electrician","hvac","renovation","laundry","pest_control","window_cleaning","carpet_cleaning","waste_removal","moving","disinfection"
                    ].map((cat) => {
                      const selected = companyForm.watch("primary_categories").includes(cat);
                      return (
                        <Badge
                          key={cat}
                          variant={selected ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => {
                            const current = companyForm.getValues("primary_categories");
                            companyForm.setValue(
                              "primary_categories",
                              selected ? current.filter((c) => c !== cat) : [...current, cat]
                            );
                          }}
                        >
                          {cat.split("_").join(" ")}
                        </Badge>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label>{t('onboard.company.service_areas','In quali città o CAP lavori?')}</Label>
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('onboard.company.service_areas_desc','Aggiungi tutte le città o i CAP in cui fornisci servizi. Una copertura ben definita migliora la qualità delle richieste ricevute.')}
                  </p>

                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      placeholder={t('onboard.company.service_areas_ph','Scrivi una città o un CAP e premi Invio')}
                      onKeyDown={(e) => {
                        const v = (e.target as HTMLInputElement).value.trim();
                        if (e.key === 'Enter' && v) {
                          e.preventDefault();
                          const current = companyForm.getValues("service_areas");
                          if (!current.includes(v)) companyForm.setValue("service_areas", [...current, v]);
                          (e.target as HTMLInputElement).value = "";
                        }
                      }}
                    />
                  </div>

                  {companyForm.watch("service_areas").length < 5 && (
                    <div className="mt-3">
                      <p className="text-xs text-muted-foreground mb-2">
                        {t('onboard.company.service_areas_suggestions','Suggerimenti vicini alla tua città principale:')}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {getNearbyCitySuggestions(companyForm.watch("city")).map((city, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="cursor-pointer text-xs hover:bg-primary hover:text-primary-foreground"
                            onClick={() => {
                              const current = companyForm.getValues("service_areas");
                              if (!current.includes(city)) {
                                companyForm.setValue("service_areas", [...current, city]);
                              }
                            }}
                          >
                            + {city}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {companyForm.watch("service_areas").length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {companyForm.watch("service_areas").map((area, idx) => (
                        <Badge
                          key={idx}
                          variant="secondary"
                          className="cursor-pointer"
                          onClick={() => {
                            const current = companyForm.getValues("service_areas").filter((a) => a !== area);
                            companyForm.setValue("service_areas", current);
                          }}
                        >
                          {area} ×
                        </Badge>
                      ))}
                    </div>
                  )}

                  {companyForm.watch("service_areas").length > 0 && (
                    <div className="mt-3 rounded-lg bg-muted/50 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {t('onboard.company.coverage_level','Livello di copertura:')}
                        </span>
                        <Badge variant={
                          companyForm.watch("service_areas").length >= 10 ? "default" :
                          companyForm.watch("service_areas").length >= 5 ? "secondary" : "outline"
                        }>
                          {companyForm.watch("service_areas").length >= 10 ? t('onboard.company.coverage_national','National') :
                           companyForm.watch("service_areas").length >= 5 ? t('onboard.company.coverage_regional','Regional') :
                           t('onboard.company.coverage_local','Local')}
                        </Badge>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {companyForm.watch("service_areas").length >= 10 ?
                          t('onboard.company.coverage_national_desc','Ottimo: il profilo mostra una copertura molto ampia.') :
                         companyForm.watch("service_areas").length >= 5 ?
                          t('onboard.company.coverage_regional_desc','Buona copertura: stai indicando un raggio operativo credibile e utile.') :
                          t('onboard.company.coverage_local_desc','Copertura locale: perfetta se vuoi partire dalla tua zona principale.')}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2 pt-2">
                  <h3 className="text-base font-semibold text-slate-900">Disponibilità standard</h3>
                  <p className="text-sm text-muted-foreground">
                    Indica in quali giorni e fasce orarie il team è normalmente disponibile per ricevere prenotazioni.
                  </p>
                </div>
                {Object.entries(availability).map(([day, schedule]) => (
                  <div key={day} className="flex items-center justify-between p-4 border rounded">
                    <div className="flex items-center gap-4">
                      <div className="w-4 h-4">
                        <input
                          type="checkbox"
                          checked={schedule.available}
                          onChange={(e) => {
                            setAvailability(prev => ({
                              ...prev,
                              [day]: {
                                ...prev[day],
                                available: e.target.checked
                              }
                            }));
                          }}
                          className="rounded"
                        />
                      </div>
                      <span className="font-medium capitalize w-20">{day}</span>
                    </div>
                    {schedule.available && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={schedule.start}
                          onChange={(e) => {
                            setAvailability(prev => ({
                              ...prev,
                              [day]: {
                                ...prev[day],
                                start: e.target.value
                              }
                            }));
                          }}
                          className="w-24"
                        />
                        <span>{t('onboard.availability.to','to')}</span>
                        <Input
                          type="time"
                          value={schedule.end}
                          onChange={(e) => {
                            setAvailability(prev => ({
                              ...prev,
                              [day]: {
                                ...prev[day],
                                end: e.target.value
                              }
                            }));
                          }}
                          className="w-24"
                        />
                      </div>
                    )}
                  </div>
                ))}
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Button variant="outline" onClick={() => setCurrentStep(3)} className="sm:flex-1">
                    Torna ai servizi
                  </Button>
                  <Button onClick={handleAvailabilitySubmit} className="sm:flex-1">
                    Continua a pagamenti e documenti
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 5 && (
          <Card className="app-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Pagamenti e documenti
              </CardTitle>
              <CardDescription>
                Ultimo passaggio operativo prima del riepilogo: confermi come funzionano incasso, payout e requisiti documentali.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  In questo passaggio ti spieghiamo in modo semplice come funzionano incasso, payout e requisiti documentali, così puoi completare l&apos;attivazione senza dubbi.
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="font-medium text-slate-900">Cosa serve per essere operativo</div>
                  <div className="mt-3 space-y-2 text-sm text-slate-700">
                    <p>Per ricevere i payout ti serviranno verifica identità/KYC, conto bancario di accredito e gestione fiscale corretta lato azienda.</p>
                    <p>Se hai già inserito Partita IVA e dati azienda nel profilo, sei già sulla strada giusta: qui stai solo confermando le regole operative.</p>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="font-medium text-slate-900">Accettazione pagamenti e payout</div>
                  <p className="mt-1 text-sm text-slate-600">
                    Ti chiediamo di confermare queste condizioni per poter gestire correttamente incassi, accrediti e responsabilità amministrative.
                  </p>
                  <div className="mt-3 flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={acceptedVendorPaymentsTerms}
                      onChange={async (e) => {
                        const next = e.target.checked;
                        const prev = acceptedVendorPaymentsTerms;
                        setAcceptedVendorPaymentsTerms(next);
                        const cid = await getOnboardingCompanyId();
                        if (!cid) {
                          setAcceptedVendorPaymentsTerms(prev);
                          toast({
                            title: "Errore",
                            description: "Completa prima i dati essenziali azienda per salvare l'accettazione.",
                            variant: "destructive",
                          });
                          return;
                        }
                        const { error } = await supabase
                          .from("companies")
                          .update({ vendor_payments_terms_accepted: next })
                          .eq("id", cid);
                        if (error) {
                          setAcceptedVendorPaymentsTerms(prev);
                          toast({
                            title: "Salvataggio non riuscito",
                            description: error.message,
                            variant: "destructive",
                          });
                        }
                      }}
                      className="mt-1"
                    />
                    <div className="space-y-2 text-sm text-slate-700">
                      <p>Accetto che Halomora possa incassare dal cliente per mio conto tramite Stripe Connect e trattenga le fee applicabili prima del payout.</p>
                      <p>Accetto che restano a mio carico obblighi fiscali, fatturazione del servizio, KYC e conto bancario di payout.</p>
                      <p>Accetto che chargeback, refund e contestazioni possano ridurre o ritardare i payout.</p>
                      <p>
                        Ho letto e accetto anche il{" "}
                        <Link to="/legal/vendor-agreement" className="text-primary underline">
                          Vendor Agreement
                        </Link>.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button variant="outline" onClick={() => setCurrentStep(4)} className="sm:flex-1">
                    Torna a zone e disponibilità
                  </Button>
                  <Button onClick={handlePaymentsStepContinue} className="sm:flex-1">
                    Continua al riepilogo finale
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 6 && (
          <Card className="app-dark-panel border border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-300" />
                {t('onboard.review.title','Riepilogo finale')}
              </CardTitle>
              <CardDescription className="text-slate-300">
                {t('onboard.review.desc','Controlla i dati raccolti prima di completare l’attivazione del profilo azienda')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="rounded-lg border border-white/10 bg-white/5 p-4 text-sm text-slate-200">
                  Dopo il completamento verrai reindirizzato alla dashboard azienda. I dati salvati in questo flusso verranno mantenuti con la stessa logica di prima: stiamo solo rendendo l&apos;onboarding più chiaro da compilare.
                </div>
                <div className="app-dark-metric p-5">
                  <h3 className="font-semibold mb-2 text-white">{t('onboard.review.company_info','Company Information')}</h3>
                  <div className="text-sm text-slate-300">
                    <p>Azienda: {companyForm.getValues("company_name")}</p>
                    <p>Email: {companyForm.getValues("email")}</p>
                    <p>Telefono: {companyForm.getValues("phone")}</p>
                  </div>
                </div>
                
                <div className="app-dark-metric p-5">
                  <h3 className="font-semibold mb-2 text-white">{t('onboard.review.services','Servizi ({{count}})').replace('{{count}}', String(services.length))}</h3>
                  <div className="space-y-2">
                    {services.map((service, index) => (
                      <div key={index} className="text-sm text-slate-300">
                        {service.name} - {formatCurrency(Number(service.base_price || 0))} ({service.pricing_type})
                      </div>
                    ))}
                  </div>
                </div>

                <div className="app-dark-metric p-5">
                  <h3 className="font-semibold mb-2 text-white">{t('onboard.review.availability','Disponibilità')}</h3>
                  <div className="space-y-1">
                    {Object.entries(availability).map(([day, schedule]) => (
                      <div key={day} className="text-sm text-slate-300">
                        <span className="capitalize">{day}:</span> {schedule.available ? `${schedule.start} - ${schedule.end}` : t('onboard.availability.not_available','Not available')}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="app-dark-metric p-5">
                  <h3 className="mb-2 font-semibold text-white">Prossimi step</h3>
                  <div className="space-y-1 text-sm text-slate-300">
                    <p>1. Completi l&apos;attivazione del profilo azienda.</p>
                    <p>2. Arrivi nella dashboard per gestire servizi, disponibilità e richieste.</p>
                    <p>3. Potrai aggiornare in seguito profilo, aree coperte e impostazioni operative.</p>
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button variant="outline" onClick={() => setCurrentStep(5)} className="sm:flex-1 border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white">
                    Torna a pagamenti e documenti
                  </Button>
                  <Button onClick={handleFinishOnboarding} className="sm:flex-1">
                    {t('onboard.actions.complete','Completa attivazione')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
